"use client";

import log from "logbeacon";
import {
  DEFAULT_GUIDE_MODAL_CONFIG,
  GuideModalConfig,
  GuideModalState,
  GuideModalType,
  LAST_SESSION_ID_KEY,
  SESSION_ID_KEY,
  getStorageKey,
} from "./types";

/**
 * 生成唯一的会话ID
 */
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 创建弹窗状态
 */
const createGuideModalState = (
  modalType: GuideModalType,
  dismissDuration: number = DEFAULT_GUIDE_MODAL_CONFIG.dismissDuration,
): GuideModalState => {
  return {
    dismissed: true,
    dismissedAt: Date.now(),
    dismissDuration,
    sessionId: generateSessionId(),
    modalType,
  };
};

/**
 * 检查时间是否已过期
 */
const isDismissExpired = (state: GuideModalState): boolean => {
  const now = Date.now();
  const durationMs = state.dismissDuration * 60 * 1000; // 转换为毫秒
  return now - state.dismissedAt >= durationMs;
};

/**
 * 检查特定弹窗类型是否为新会话
 */
const isNewSessionForModal = (modalType: GuideModalType): boolean => {
  try {
    const currentSessionId = sessionStorage.getItem(SESSION_ID_KEY);
    const modalSessionKey = `${modalType}_session_id`;
    const lastModalSessionId = localStorage.getItem(modalSessionKey);

    log.debug("[isNewSessionForModal] 调试：", {
      modalType,
      currentSessionId,
      lastModalSessionId,
      modalSessionKey,
    });

    // If there is no current session id, generate one and treat as new session
    if (!currentSessionId) {
      const newSessionId = generateSessionId();
      sessionStorage.setItem(SESSION_ID_KEY, newSessionId);
      return true;
    }

    // If there is no last modal session id, initialize with current and treat as new session
    if (!lastModalSessionId) {
      localStorage.setItem(modalSessionKey, currentSessionId);
      return true;
    }

    // If current session id differs from last modal session id, it's a new session for this modal
    const isNew = currentSessionId !== lastModalSessionId;

    return isNew;
  } catch (error) {
    log.error("[GuideModal] Error in isNewSessionForModal:", error);
    // 出错时默认认为是新会话
    return true;
  }
};

/**
 * 更新特定弹窗类型的会话ID
 */
const updateSessionIdForModal = (modalType: GuideModalType): void => {
  const currentSessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (currentSessionId) {
    const modalSessionKey = `${modalType}_session_id`;
    localStorage.setItem(modalSessionKey, currentSessionId);
  }
};

/**
 * 检查路径是否匹配目标页面（支持国际化路径）
 *
 * @param currentPath 当前页面路径
 * @param targetPages 目标页面路径数组
 * @returns 是否匹配
 */
const isPathMatch = (currentPath: string, targetPages: string[]): boolean => {
  return targetPages.some((targetPage) => {
    // 直接匹配
    if (currentPath.startsWith(targetPage)) {
      return true;
    }

    // 支持国际化路径匹配
    // 支持的语言代码格式：
    // - 2位语言代码：en, zh, ko, ja, fr, es, de, it, pt, ru, ar, hi, th, vi, tr
    // - 复合语言代码：zh-cn, zh-tw, en-us, en-gb, fr-ca, es-mx, pt-br 等
    const localePattern = /^\/([a-z]{2}(?:-[a-z]{2})?)(\/.*)?$/;
    const match = currentPath.match(localePattern);

    if (match) {
      const pathWithoutLocale = match[2] || "/";
      return pathWithoutLocale.startsWith(targetPage);
    }

    return false;
  });
};

/**
 * 检查是否应该显示弹窗
 *
 * @param config 配置选项
 * @param currentPath 当前页面路径
 * @returns 是否应该显示弹窗
 */
export const shouldShowGuideModal = (
  config: GuideModalConfig = DEFAULT_GUIDE_MODAL_CONFIG,
  currentPath: string = typeof window !== "undefined"
    ? window.location.pathname
    : "/",
): boolean => {
  try {
    // 0. 先检查是否命中排除页面（支持国际化路径）
    if (config.excludedPages && config.excludedPages.length > 0) {
      const isExcluded = isPathMatch(currentPath, config.excludedPages);
      if (isExcluded) {
        return false;
      }
    }

    // 1. 检查是否为目标页面（支持国际化路径）
    const isTargetPage = isPathMatch(currentPath, config.targetPages);

    if (!isTargetPage) {
      return false;
    }

    // 2. 检查是否为新会话（使用特定弹窗类型的会话检测）
    const isNew = isNewSessionForModal(config.modalType);
    log.debug(
      "[isNewSessionForModal] isNew:",
      isNew,
      "for modal:",
      config.modalType,
    );
    if (!isNew) {
      return false;
    }
    // 3. 检查存储状态
    const storageKey = getStorageKey(config.modalType);
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const state: GuideModalState = JSON.parse(stored);
        if (config.enableTimeControl && !isDismissExpired(state)) {
          return false;
        }
      } catch (error) {
        log.warn("[文案]Failed to parse guide modal state:", error);
        // 如果解析失败，清除无效数据
        localStorage.removeItem(storageKey);
      }
    }

    return true;
  } catch (error) {
    log.error("[GuideModal] Error in shouldShowGuideModal:", error);
    return false;
  }
};

/**
 * 关闭弹窗
 */
export const dismissGuideModal = (
  modalType: GuideModalType,
  dismissDuration: number = DEFAULT_GUIDE_MODAL_CONFIG.dismissDuration,
): void => {
  const state = createGuideModalState(modalType, dismissDuration);
  const storageKey = getStorageKey(modalType);
  localStorage.setItem(storageKey, JSON.stringify(state));
  // 只更新特定弹窗类型的会话ID，不影响其他弹窗
  updateSessionIdForModal(modalType);
};

/**
 * 清除弹窗状态
 */
const clearGuideModalState = (modalType: GuideModalType): void => {
  const storageKey = getStorageKey(modalType);
  localStorage.removeItem(storageKey);
  localStorage.removeItem(LAST_SESSION_ID_KEY);
  sessionStorage.removeItem(SESSION_ID_KEY);
};

export type { GuideModalConfig } from "./types";
export { DEFAULT_GUIDE_MODAL_CONFIG, GuideModalType } from "./types";

// 全局调试函数
if (typeof window !== "undefined") {
  (window as any).debugGuideModal = {
    // 获取当前状态
    getState: (modalType: GuideModalType) => {
      const currentSessionId = sessionStorage.getItem(SESSION_ID_KEY);
      const lastSessionId = localStorage.getItem(LAST_SESSION_ID_KEY);
      const storageKey = getStorageKey(modalType);
      const stored = localStorage.getItem(storageKey);

      return {
        modalType,
        storageKey,
        currentSessionId,
        lastSessionId,
        stored: stored ? JSON.parse(stored) : null,
        currentPath: window.location.pathname,
        timestamp: new Date().toISOString(),
      };
    },

    // 清除所有状态
    clearAll: (modalType?: GuideModalType) => {
      if (modalType) {
        clearGuideModalState(modalType);
      } else {
        // 清除所有弹窗类型的状态
        Object.values(GuideModalType).forEach((type) => {
          clearGuideModalState(type);
        });
      }
      log.debug("[文案]🔍 [GuideModal] state cleared");
    },

    // 测试显示逻辑
    testShow: (config: GuideModalConfig) => {
      const result = shouldShowGuideModal(config, window.location.pathname);
      log.debug("[文案]🔍 [GuideModal] test result:", {
        config,
        currentPath: window.location.pathname,
        shouldShow: result,
      });
      return result;
    },

    // 模拟新会话
    simulateNewSession: () => {
      sessionStorage.removeItem(SESSION_ID_KEY);
      localStorage.removeItem(LAST_SESSION_ID_KEY);
      log.debug("[文案]🔍 [GuideModal] simulated new session");
    },
  };
}
